import { useEffect, useState } from 'react';
import { Building2, Loader2, LogOut, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { emisoresApi } from '../utils/api';
import { AgregarEmpresaModal } from '../pages/configuracion/ListaEmpresas';

/**
 * Pantalla para un usuario con sesión pero sin empresa elegida.
 *
 * Sin empresa no se dibuja el `Layout` (menú, selector), así que esta pantalla
 * tiene que bastarse sola: una cuenta recién creada desde el SuperAdmin nace sin
 * empresas, y su administrador crea aquí la primera. Si ya hay empresas pero
 * ninguna elegida, se elige aquí. Siempre se puede cerrar la sesión.
 */
export default function SinEmpresa() {
  const { user, emisores, setEmisores, setCurrentEmisor, logout } = useAuthStore();
  const [lista, setLista] = useState(emisores || []);
  const [cargando, setCargando] = useState(true);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const esAdmin = user?.role === 'admin';

  // Lo guardado en la sesión puede estar viejo: se vuelve a pedir la lista
  const cargar = async () => {
    const response = await emisoresApi.getMy();
    const nuevas = response.data?.data || [];
    setLista(nuevas);
    setEmisores(nuevas);
    return nuevas;
  };

  useEffect(() => {
    cargar()
      .catch((error) => console.error('Error loading emisores:', error))
      .finally(() => setCargando(false));
  }, []);

  const elegir = (emisor) => {
    localStorage.setItem('emisorId', emisor.id);
    setCurrentEmisor(emisor);
  };

  const alCrear = async (creada) => {
    setMostrarAlta(false);
    toast.success('Empresa creada exitosamente');
    try {
      const nuevas = await cargar();
      elegir(nuevas.find((e) => String(e.id) === String(creada.id)) || creada);
    } catch {
      elegir(creada);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md w-full">
        <div className="h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building2 className="h-8 w-8 text-gray-400" />
        </div>

        {cargando ? (
          <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto" />
        ) : lista.length > 0 ? (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Seleccione una empresa</h2>
            <p className="text-gray-500 mb-6">Elija la empresa con la que desea trabajar.</p>
            <div className="space-y-2 text-left">
              {lista.map((e) => (
                <button
                  key={e.id}
                  onClick={() => elegir(e)}
                  className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900">{e.nombre}</p>
                  <p className="text-xs text-gray-500">{e.identificacion}</p>
                </button>
              ))}
            </div>
          </>
        ) : esAdmin ? (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Cree la primera empresa</h2>
            <p className="text-gray-500 mb-6">
              Su cuenta todavía no tiene empresas. Agregue la primera con su cédula para empezar a trabajar.
            </p>
            <button
              onClick={() => setMostrarAlta(true)}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Crear la primera empresa
            </button>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sin empresas asignadas</h2>
            <p className="text-gray-500">
              Su usuario todavía no tiene ninguna empresa. Pídale al administrador de su cuenta que le asigne una.
            </p>
          </>
        )}

        <div className="mt-8">
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </button>
        </div>
      </div>

      {mostrarAlta && (
        <AgregarEmpresaModal onClose={() => setMostrarAlta(false)} onSuccess={alCrear} />
      )}
    </div>
  );
}
